import React, { ReactElement } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import CheckIcon from '@material-ui/icons/Check';
import ArrowDropDownRoundedIcon from '@material-ui/icons/ArrowDropDownRounded';
import Autocomplete, { AutocompleteRenderOptionState } from '@material-ui/lab/Autocomplete';
import useStyles, { StyledTextFieldContainer, ErrorMsg, DropdownContainer, HelperText } from './Dropdown.styles';
import { noop } from 'lodash';

export interface DropdownProps<T = unknown> {
  data: T[];
  disabled?: boolean;
  placeholder?: string;
  dataQaId: string;
  renderInput?: ReactElement;
  renderOption: (item: T) => string | React.ReactElement;
  renderSelectedOption: (item: T) => string;
  getOptionSelected?: (option: T, value: T) => boolean;
  value: T | null;
  open?: boolean;
  onChange: (item: T) => void;
  onInputChange?: (value: string) => void;
  onOpen?: (event: object) => void;
  onClose?: (event: object) => void;
  title?: string;
  noOptionsText?: string;
  loadingText?: string;
  showPopUpIcon?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  errorMsg?: string;
  helperText?: string;
  renderHelper?: () => React.ReactElement;
  disableClearable?: boolean;
  ariaLabel?: string;
  onNearBottom?: () => void;
  nearBottomTriggervalue?: number;
}

const Dropdown = <T extends {}>({
  data,
  disabled,
  placeholder,
  dataQaId,
  renderOption,
  renderSelectedOption,
  getOptionSelected,
  value,
  onChange,
  open,
  onInputChange = noop,
  onOpen = noop,
  onClose = noop,
  title,
  readOnly = true,
  showPopUpIcon = true,
  noOptionsText = 'No options',
  loadingText = 'Loading...',
  loading = false,
  errorMsg,
  helperText,
  renderHelper,
  disableClearable = true,
  ariaLabel,
  onNearBottom,
  nearBottomTriggervalue = 25,
}: DropdownProps<T>): JSX.Element => {
  const classes = useStyles();

  const onValueChange = (_event: object, item: T | null): void => {
    if (item) {
      onChange(item);
    }
  };

  const onInputValueChange = (_event: object, value: string): void => {
    if (onInputChange) {
      onInputChange(value);
    }
  };
  const onDropdownOpen = (event: object): void => {
    if (onOpen) {
      onOpen(event);
    }
  };
  const onDropdownClose = (event: object): void => {
    if (onClose) {
      onClose(event);
    }
  };

  const renderOptionItem = (option: T, state: AutocompleteRenderOptionState): ReactElement => {
    const item = renderOption(option);
    const itemName = renderSelectedOption(option);
    return state.selected ? (
      <div className={classes.selectedOptionContainer} role="listitem" data-qa-id={`select-option-${itemName}`}>
        {item}
        <CheckIcon data-qa-id={`selected-option-icon`} className={classes.selectedIcon} />
      </div>
    ) : (
      <div role="listitem" data-qa-id={`select-option-${itemName}`}>
        {item}
      </div>
    );
  };
  const openProp = { open: typeof open === 'boolean' ? open : undefined };

  return (
    <DropdownContainer>
      <Autocomplete
        aria-label={'autocomplete'}
        options={data}
        {...openProp}
        renderOption={renderOptionItem}
        getOptionLabel={renderSelectedOption}
        getOptionSelected={getOptionSelected}
        classes={{
          option: classes.option,
          inputRoot: classes.inputRoot,
          input: classes.input,
          popupIndicator: classes.popupIndicator,
          endAdornment: classes.endAdornment,
          paper: classes.paper,
          listbox: classes.listbox,
          clearIndicator: classes.clearIndicator,
        }}
        openOnFocus={true}
        blurOnSelect={true}
        selectOnFocus={!readOnly}
        disableClearable={disableClearable}
        disabled={disabled}
        disablePortal={true}
        popupIcon={showPopUpIcon ? <ArrowDropDownRoundedIcon fontSize="large" /> : null}
        value={value}
        onChange={onValueChange}
        onInputChange={onInputValueChange}
        onOpen={onDropdownOpen}
        onClose={onDropdownClose}
        forcePopupIcon={true}
        fullWidth
        title={title}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        role="listbox"
        renderInput={(params: TextFieldProps): ReactElement => (
          <StyledTextFieldContainer className="text-field-container">
            <TextField
              {...params}
              style={{ cursor: 'pointer' }}
              inputProps={{
                ...params.inputProps,
                readOnly,
                'data-qa-id': dataQaId,
                id: dataQaId,
                'aria-label': ariaLabel || 'picker',
              }}
              placeholder={placeholder}
              variant="outlined"
              error={!!errorMsg}
            />
          </StyledTextFieldContainer>
        )}
        ListboxProps={getListboxProps(dataQaId, nearBottomTriggervalue, onNearBottom)}
      />
      {!errorMsg && helperText && <HelperText className="helperText">{helperText}</HelperText>}
      {!errorMsg && renderHelper && <HelperText className="helperText">{renderHelper()}</HelperText>}
      <ErrorMsg className="errorMsg">{errorMsg}</ErrorMsg>
    </DropdownContainer>
  );
};

function getListboxProps(dataQaId: string, nearBottomTriggervalue: number, onNearBottom?: () => void) {
  const handleScroll = ({ currentTarget }: React.SyntheticEvent) => {
    if (!onNearBottom) {
      return false;
    }

    const { scrollHeight, scrollTop, clientHeight } = currentTarget;
    const leftToReachBottom = scrollHeight - (scrollTop + clientHeight);
    if (leftToReachBottom <= nearBottomTriggervalue) {
      onNearBottom();
    }
  };

  return {
    onScroll: handleScroll,
    'data-qa-id': `${dataQaId}-listbox`,
    'aria-label': 'listbox',
    'aria-labelledby': undefined,
    tabIndex: 0,
  };
}

export default Dropdown;
